// Meter ballistics and scale (P4.7) — FR-14's channel meters and FR-16's master.
//
// P4.7's rule: *"Ballistics are a decision — a meter that falls instantly is unreadable and one that
// falls slowly lies about transients; state the choice."* This file is the choice, stated, with the
// reasoning attached and a test per clause. It is in `platform/types` for `mixer.ts`'s reason: the
// UI draws with it and the tests measure with it, and neither should own it.
//
// ============================================================================
// THE DECISION
// ============================================================================
//
// **Rise is instantaneous. Fall is 20 dB per second, linear on the dB scale.**
//
// *Rise instantaneous*, because the entire purpose of a peak meter is the transient. A meter with an
// attack time cannot show a sample that was over for 2 ms, which is exactly the sample that matters —
// and the reading it replaces it with is not "slightly late", it is *lower*, which reads as headroom
// that does not exist. There is no attack constant here, and its absence is the design.
//
// *Fall at a constant rate in dB*, not an exponential decay of the amplitude. This is the clause most
// likely to be "simplified" later, so: an exponential amplitude decay is a *constant* on a linear
// scale and an accelerating crawl on a dB one — the bar would drop fast through the top of its travel
// and creep through the bottom, so the same 6 dB of movement would take a different time depending on
// where it happened. A constant dB/s falls at a constant *visual* speed, which is what a hand reading
// it is actually judging.
//
// *20 dB/s* is the number, and it was chosen against the music rather than against a standard. At
// 120 BPM a kick lands every 500 ms, so the meter falls 10 dB between kicks: clearly moving, never
// reaching the floor, never pinned. 40 dB/s empties the bar between beats and strobes; 10 dB/s leaves
// it hanging near the peak and stops distinguishing a loud passage from a quiet one. IEC 60268-18's
// PPM falls slower than this (~11.8 dB/s) because it is built for broadcast compliance, where the
// question is "did it go over" rather than "what is the mix doing right now".
//
// ⚠️ **This is a by-ear-and-by-eye decision that has not yet been looked at on a moving mix**, which
// is the honest status: the reasoning above is sound and the number is a starting point. It is one
// constant, in one place, for exactly that reason.

/**
 * The bottom of the scale, in dBFS.
 *
 * −60 dB rather than −∞: below this a meter is showing noise and the bar is one pixel either way.
 * The same floor `Transport.tsx` has used for the deck meter since P2.9.
 */
export const METER_FLOOR_DB = -60;

/**
 * The top of the scale, in dBFS — **above full scale, deliberately.**
 *
 * ⚠️ **A master meter that stopped at 0 would undo the reason the tap is where it is.** P4.6 put the
 * measurement *before* the limiter precisely so an overload is visible; a scale that clipped at 0
 * would pin the bar at the top and show exactly what a post-limiter meter shows, which is nothing.
 * +6 dB is the figure `mixer.ts`'s headroom section names for two correlated decks at unity through
 * the sharp crossfader at centre — the loudest case the design admits — so the scale covers what the
 * bus can actually produce.
 */
export const METER_TOP_DB = 6;

/** Where 0 dBFS sits as a fraction of the bar — the mark the top zone is measured from. */
export const METER_UNITY_FRACTION =
  (0 - METER_FLOOR_DB) / (METER_TOP_DB - METER_FLOOR_DB);

/** The fall rate. See the header: constant in dB, chosen against a 120 BPM kick. */
export const METER_FALL_DB_PER_SECOND = 20;

/**
 * The longest step the fall is allowed to take, in seconds.
 *
 * ⚠️ **A tab that was in the background for a minute comes back with a `dt` of a minute**, and
 * without this the meter would fall 1200 dB in one step — harmless arithmetically, since the result
 * clamps at the floor, but it makes the first visible frame after a tab switch a bar that has already
 * emptied rather than one that catches up over a few frames. Capping the step at four frames' worth
 * makes a resumed tab behave like a slow one, which is what it is.
 */
export const METER_MAX_STEP_SECONDS = 4 / 60;

/**
 * Linear amplitude → dBFS, with the floor applied.
 *
 * Zero and negatives return the floor rather than `-Infinity`, because the caller's next step is
 * arithmetic and `-Infinity` propagates through it silently.
 */
export function meterDb(amplitude: number): number {
  if (!(amplitude > 0)) {
    return METER_FLOOR_DB;
  }
  const db = 20 * Math.log10(amplitude);
  return db < METER_FLOOR_DB ? METER_FLOOR_DB : db;
}

/**
 * dBFS → a 0…1 bar fraction.
 *
 * Decibels rather than linear because hearing is logarithmic: on a linear bar the whole useful range
 * of a quiet passage lives in the first few pixels.
 */
export function meterFraction(db: number): number {
  const span = METER_TOP_DB - METER_FLOOR_DB;
  const fraction = (db - METER_FLOOR_DB) / span;
  return fraction < 0 ? 0 : fraction > 1 ? 1 : fraction;
}

/**
 * One frame of ballistics: jump to `targetDb` if it is louder, otherwise fall towards it.
 *
 * `dtSeconds` is the real elapsed time rather than an assumed 1/60, so the meter falls at the same
 * rate on a 144 Hz display as on a 60 Hz one and does not accelerate when frames are dropped. A
 * meter whose speed depended on the frame rate would be a different instrument on every machine.
 */
export function meterFall(
  currentDb: number,
  targetDb: number,
  dtSeconds: number,
): number {
  if (targetDb >= currentDb) {
    return targetDb;
  }
  const step = Math.min(Math.max(dtSeconds, 0), METER_MAX_STEP_SECONDS);
  const fallen = currentDb - METER_FALL_DB_PER_SECOND * step;
  return fallen < targetDb ? targetDb : fallen;
}

/**
 * How long the peak marker sits at a new maximum before it starts falling, in seconds.
 *
 * ⚠️ **Added at P4.8 because the by-eye check said the marker flickered, and it was right.** P4.7
 * gave the marker the same ballistics as the bar and justified it: "a marker that fell at a different
 * rate would open and close a gap that means nothing, when the gap between peak and RMS is exactly
 * the crest factor". Sound reasoning about the *numbers*, and wrong about the *instrument* — peak is
 * far spikier than RMS, so a thin 2 px line with instantaneous rise and an immediate fall chases
 * every transient and reads as jitter. The stakeholder watched it on real music and said the gradient
 * bar looked fine and the line did not. That is the whole reason the check exists.
 *
 * ⚠️ **0.25 s, and it was 1 s first — which read as "very slow" for a reason worth keeping.** A hold
 * longer than the gap between peaks *never expires*: at 120 BPM a kick lands every 500 ms, so every
 * one re-armed the 1 s hold before the previous had run out and the marker simply froze at the
 * running maximum. That is not a hold, it is a latch. **The hold must be shorter than the interval
 * between the peaks it is holding**, or the meter stops being a meter.
 *
 * 0.25 s is under one beat at every tempo this app can play — a beat is 500 ms at 120 BPM and still
 * 345 ms at 174 — so the marker holds through the transient cluster of a single drum hit, visibly
 * decays in the gap, and jumps again on the next. It moves *with* the music instead of chasing every
 * hi-hat or sitting still.
 *
 * It still jumps **instantly** to anything louder: the hold delays the fall, never the rise, so the
 * marker cannot under-read a transient, which is the one thing a peak meter must not do.
 */
export const METER_PEAK_HOLD_SECONDS = 0.25;

/** A peak marker's position and how much of its hold remains. Caller-owned and mutated in place. */
export interface PeakHoldState {
  db: number;
  holdSeconds: number;
}

/**
 * Advances a peak marker: jump up instantly, hold, then fall at the normal rate.
 *
 * Mutates rather than returning, because two numbers cannot be returned without an object and this
 * runs once per meter per animation frame — `MeterFrame` and `readFramePeaks` are the same shape for
 * the same reason.
 */
export function meterPeakHold(
  state: PeakHoldState,
  targetDb: number,
  dtSeconds: number,
): void {
  if (targetDb >= state.db) {
    state.db = targetDb;
    state.holdSeconds = METER_PEAK_HOLD_SECONDS;
    return;
  }
  if (state.holdSeconds > 0) {
    state.holdSeconds -= Math.max(0, dtSeconds);
    return;
  }
  state.db = meterFall(state.db, targetDb, dtSeconds);
}

/**
 * Limiter gain reduction (linear, 1 = untouched) → dB of reduction, as a positive number.
 *
 * Positive because a GR meter reads "how much is coming off", and every piece of hardware draws it
 * as a bar growing downwards from zero rather than a negative number growing upwards.
 */
export function gainReductionDb(gain: number): number {
  if (!(gain > 0) || gain >= 1) {
    return gain >= 1 ? 0 : GAIN_REDUCTION_MAX_DB;
  }
  const db = -20 * Math.log10(gain);
  return db > GAIN_REDUCTION_MAX_DB ? GAIN_REDUCTION_MAX_DB : db;
}

/**
 * The top of the gain-reduction scale, in dB.
 *
 * 12 dB, because the limiter pulling more than that is not a louder reading, it is a different
 * problem — the gain staging is wrong and the number to look at is the master gain, not the meter.
 * A scale that kept going would make "very bad" and "catastrophic" look like neighbours.
 */
export const GAIN_REDUCTION_MAX_DB = 12;

/** Gain reduction in dB → a 0…1 bar fraction of its own scale. */
export function gainReductionFraction(db: number): number {
  const fraction = db / GAIN_REDUCTION_MAX_DB;
  return fraction < 0 ? 0 : fraction > 1 ? 1 : fraction;
}
